import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import {
  ChevronDown,
  ChevronUp,
  Info,
  FileSpreadsheet,
  Search,
  ArrowUpRight,
  TrendingUp,
  Receipt,
  ShoppingBag,
  ShoppingCart,
  CheckCircle2,
  Filter,
  BarChart3,
  Layers,
  Sparkles,
  X,
  BookOpen,
} from 'lucide-react';

interface FinancialReportsDashboardProps {
  onSelectReport?: (reportId: string) => void;
}

interface ReportItem {
  name: string;
  category: 'ar' | 'ap' | 'advanced' | 'other' | 'gstr';
  hasCsv?: boolean;
  csvOnly?: boolean;
  targetId: string;
  description: string;
  badge?: string;
  usedFor?: string[];
  contains?: string[];
  formula?: string;
}

const ALL_REPORTS: ReportItem[] = [
  // ── Sales Reports ──────────────────────────────────────────────────────────
  {
    name: 'Proforma Invoices',
    category: 'ar',
    hasCsv: false,
    targetId: 'sales-register',
    description: 'Preliminary non-accounting invoice sent to customer before actual billing.',
    usedFor: ['Providing price quotations', 'Customs documentation', 'Customer approval before billing'],
    contains: ['Quotation No', 'Customer Name', 'Quotation Date', 'Estimated Amount'],
  },
  {
    name: 'Proforma Invoice Register & Status',
    category: 'ar',
    hasCsv: true,
    targetId: 'sales-register',
    description: 'Comprehensive report showing all proforma invoices created and their conversion status.',
    usedFor: ['Track all quotations before they become actual invoices', 'Monitor sales proposal conversion rates'],
    contains: ['Proforma Number', 'Customer', 'Date', 'Amount', 'Status (Draft, Sent, Converted, Expired)'],
  },
  {
    name: 'Invoices Register',
    category: 'ar',
    hasCsv: true,
    targetId: 'sales-register',
    description: 'Complete list of all sales invoices issued to customers with payment statuses.',
    usedFor: ['Maintain complete sales invoice history', 'Audit sales revenue & receivables'],
    contains: ['Invoice Number', 'Customer', 'Invoice Date', 'Due Date', 'GST Amount', 'Total Amount', 'Payment Status'],
  },
  {
    name: 'Client Master Report',
    category: 'ar',
    hasCsv: true,
    targetId: 'ar-ap-ageing',
    description: 'Master list of every customer with active status, contact details, and credit limits.',
    usedFor: ['View customer information in one place', 'Review client credit limits & active accounts'],
    contains: ['Customer Name', 'GSTIN', 'Contact Details', 'Credit Limit', 'Outstanding Balance', 'Active/Inactive Status'],
  },
  {
    name: 'Payment Receipts Report',
    category: 'ar',
    hasCsv: true,
    targetId: 'cash-bank-book',
    description: 'Shows all payments received from customers across bank and cash modes.',
    usedFor: ['Track customer collections', 'Reconcile bank deposits & cash receipts'],
    contains: ['Receipt Number', 'Customer', 'Invoice Reference', 'Payment Mode', 'Amount', 'Date'],
  },
  {
    name: 'TDS Deduction (Sales)',
    category: 'ar',
    hasCsv: true,
    targetId: 'tax-summary',
    description: 'Shows Tax Deducted at Source (TDS) deducted by customers from sales payments.',
    usedFor: ['Track TDS credits receivable (Form 26AS reconciliation)', 'Adjust statutory tax credits'],
    contains: ['Customer Name', 'Invoice Value', 'TDS Rate %', 'TDS Deducted', 'Net Amount Received'],
  },
  {
    name: 'Sales Line Item Report',
    category: 'ar',
    hasCsv: true,
    targetId: 'sales-register',
    description: 'Shows every item sold rather than invoice totals for granular volume analysis.',
    usedFor: ['Analyze product-wise sales', 'Review volume discounts & selling price trends'],
    contains: ['Product / Item', 'Quantity', 'Selling Price', 'Discount', 'Tax', 'Customer'],
  },
  {
    name: 'HSN/SAC Sales Summary',
    category: 'ar',
    hasCsv: true,
    targetId: 'tax-summary',
    description: 'Summarizes sales based on HSN or SAC codes for GST tax compliance.',
    usedFor: ['GST Return filing (GSTR-1 HSN summary)', 'Statutory tax audit reporting'],
    contains: ['HSN / SAC Code', 'Description', 'Quantity', 'Taxable Value', 'GST Amount'],
  },
  {
    name: 'Credit Note GST Report',
    category: 'ar',
    hasCsv: true,
    targetId: 'tax-summary',
    description: 'Lists GST credit notes issued for goods returned, wrong billing, or price adjustments.',
    usedFor: ['Goods returned adjustments', 'Wrong billing corrections', 'Adjust GST sales liability'],
    contains: ['Credit Note No', 'Original Invoice Ref', 'Customer', 'Reason', 'Taxable Value', 'GST Adjusted'],
  },
  {
    name: 'GST Outward Sales Report',
    category: 'ar',
    hasCsv: true,
    targetId: 'tax-summary',
    description: 'Shows total GST collected on sales broken down by CGST, SGST, and IGST.',
    usedFor: ['Calculate GST payable', 'Reconcile sales register with GST portal'],
    contains: ['Taxable Value', 'CGST Amount', 'SGST Amount', 'IGST Amount', 'Total Tax Collected'],
  },
  {
    name: 'GSTR-1 Sales Report',
    category: 'ar',
    badge: 'GST',
    hasCsv: true,
    targetId: 'gstr-1',
    description: 'A report matching the exact format required for statutory GSTR-1 return filing.',
    usedFor: ['Submit GST outward supplies monthly return', 'Upload sales data to GST portal'],
    contains: ['B2B Sales', 'B2C Sales', 'Exports', 'Debit/Credit Notes'],
  },
  {
    name: 'Client Ageing Analysis',
    category: 'ar',
    hasCsv: true,
    targetId: 'ar-ap-ageing',
    description: 'Shows how long customer payments have been overdue in 30/60/90+ day buckets.',
    usedFor: ['Identify overdue receivables', 'Prioritize debt collection & credit management'],
    contains: ['Customer Name', 'Current', '0–30 Days', '31–60 Days', '61–90 Days', '90+ Days', 'Total Outstanding'],
  },
  {
    name: 'Client Outstanding Summary',
    category: 'ar',
    hasCsv: true,
    targetId: 'ar-ap-ageing',
    description: 'Shows total unpaid balances customer-wise across all active accounts.',
    usedFor: ['Know who still owes money', 'Audit accounts receivable portfolio'],
    contains: ['Customer Name', 'Contact Phone', 'Total Outstanding', 'Last Payment Date'],
  },
  {
    name: 'Credit Limit Utilization',
    category: 'ar',
    hasCsv: true,
    targetId: 'ar-ap-ageing',
    description: 'Shows how much credit each customer has used against their approved credit limit.',
    usedFor: ['Prevent exceeding customer credit limits', 'Flag credit hold accounts'],
    contains: ['Customer Name', 'Credit Limit', 'Current Outstanding', 'Available Credit', 'Utilization %'],
  },
  {
    name: 'Tag-wise Sales Audit',
    category: 'ar',
    hasCsv: true,
    targetId: 'day-book',
    description: 'Sales grouped using custom tags such as region, salesperson, or campaign.',
    usedFor: ['Analyze sales from different business perspectives', 'Audit regional & campaign ROI'],
    contains: ['Tag Name', 'Region / Salesperson', 'Product Category', 'Total Sales Volume'],
  },

  // ── Purchase Reports ───────────────────────────────────────────────────────
  {
    name: 'Purchase Order Register',
    category: 'ap',
    hasCsv: true,
    targetId: 'purchases-register',
    description: 'Shows all purchase orders sent to suppliers with fulfillment status.',
    usedFor: ['Track purchasing activity', 'Monitor vendor order commitments'],
    contains: ['PO Number', 'Vendor Name', 'Order Date', 'Amount', 'Status'],
  },
  {
    name: 'Purchases & Expenses Log',
    category: 'ap',
    hasCsv: true,
    targetId: 'purchases-register',
    description: 'Lists all purchase bills, vendor invoices, and operational business expenses.',
    usedFor: ['Monitor operational spending', 'Audit vendor bill approvals'],
    contains: ['Bill Number', 'Vendor Name', 'Category', 'Bill Date', 'Due Date', 'Total Amount'],
  },
  {
    name: 'Vendor Directory Report',
    category: 'ap',
    hasCsv: true,
    targetId: 'ar-ap-ageing',
    description: 'Master list of suppliers with GSTIN, terms, and outstanding balances.',
    usedFor: ['Manage vendor information', 'Verify supplier GSTIN & credit terms'],
    contains: ['Vendor Details', 'GST Number', 'Outstanding Balance', 'Status'],
  },
  {
    name: 'Disbursement Payment Report',
    category: 'ap',
    hasCsv: true,
    targetId: 'cash-bank-book',
    description: 'Shows all outgoing payments made to suppliers and vendors.',
    usedFor: ['Track outgoing payments', 'Reconcile vendor payment vouchers'],
    contains: ['Payment Date', 'Vendor', 'Invoice Ref', 'Payment Mode', 'Amount'],
  },
  {
    name: 'TDS Payable Report',
    category: 'ap',
    hasCsv: true,
    targetId: 'tax-summary',
    description: 'Shows Tax Deducted at Source (TDS) deducted while paying vendors.',
    usedFor: ['Track TDS payable to the government', 'Generate Form 16A TDS certificates'],
    contains: ['Vendor Name', 'Section', 'Bill Value', 'TDS Rate %', 'TDS Withheld', 'Due Date'],
  },
  {
    name: 'Purchase Line Item Report',
    category: 'ap',
    hasCsv: true,
    targetId: 'purchases-register',
    description: 'Lists every purchased item for product-wise cost analysis.',
    usedFor: ['Analyze purchases product-wise', 'Track material procurement costs'],
    contains: ['Product / Item', 'Vendor', 'Quantity', 'Rate', 'Tax Amount', 'Total Value'],
  },
  {
    name: 'HSN/SAC Purchase Summary',
    category: 'ap',
    hasCsv: true,
    targetId: 'tax-summary',
    description: 'Summarizes inward purchases by HSN/SAC code for Input Tax Credit.',
    usedFor: ['GST reporting and ITC calculations', 'Inward supply tax audit'],
    contains: ['HSN / SAC Code', 'Description', 'Quantity', 'Taxable Value', 'GST Amount'],
  },
  {
    name: 'GST Inward Purchase Report',
    category: 'ap',
    hasCsv: true,
    targetId: 'tax-summary',
    description: 'Shows GST paid on purchases for Input Tax Credit computation.',
    usedFor: ['Calculate Input Tax Credit (ITC)', 'Prepare GSTR-3B ITC claims'],
    contains: ['Bill No', 'Vendor GSTIN', 'Taxable Value', 'CGST Paid', 'SGST Paid', 'IGST Paid'],
  },
  {
    name: 'GSTR-2B ITC Match Report',
    category: 'ap',
    badge: 'GST',
    hasCsv: true,
    targetId: 'gstr-2b',
    description: 'Compares purchase records with the auto-generated GSTR-2B portal statement.',
    usedFor: ['Identify missing vendor invoices', 'Spot ITC mismatches', 'Prevent incorrect GST claims'],
    contains: ['Vendor GSTIN', 'Invoice No', 'Book ITC', 'GSTR-2B ITC', 'Mismatch Status'],
  },
  {
    name: 'Vendor Ageing Analysis',
    category: 'ap',
    hasCsv: true,
    targetId: 'ar-ap-ageing',
    description: 'Shows overdue payments owed to suppliers bucketed by age.',
    usedFor: ['Manage payables efficiently', 'Plan vendor cash disbursements & maintain credit standing'],
    contains: ['Vendor Name', 'Current', '1–30 Days', '31–60 Days', '61–90 Days', '90+ Days', 'Total Owed'],
  },
  {
    name: 'Vendor Outstanding Summary',
    category: 'ap',
    hasCsv: true,
    targetId: 'ar-ap-ageing',
    description: 'Shows pending balances payable to vendors across all supplier accounts.',
    usedFor: ['Know how much is owed to suppliers', 'Plan cashflow budgets'],
    contains: ['Vendor Name', 'Contact Phone', 'GSTIN', 'Total Outstanding', 'Payment Terms'],
  },

  // ── Financial Reports (Core) ───────────────────────────────────────────────
  {
    name: 'Trial Balance',
    category: 'advanced',
    badge: 'Core',
    hasCsv: true,
    targetId: 'trial-balance',
    description: 'Lists every ledger account with debit and credit balances to verify double-entry balance.',
    usedFor: ['Verify that total debits equal total credits before preparing financial statements', 'Ensure accounting ledger balance'],
    contains: ['Account Code', 'Account Name', 'Type', 'Debit', 'Credit', 'Closing Balance'],
  },
  {
    name: 'Balance Sheet',
    category: 'advanced',
    badge: 'Core',
    hasCsv: true,
    targetId: 'balance-sheet',
    formula: 'Assets = Liabilities + Equity',
    description: 'Shows the financial position of the business at a specific date.',
    usedFor: ['Understand what the company owns and owes', 'Review company solvency & equity'],
    contains: ['Assets (Current & Fixed)', 'Liabilities (Current & Long-term)', "Owner's Equity & Earnings"],
  },
  {
    name: 'Income Statement (P&L)',
    category: 'advanced',
    badge: 'Core',
    hasCsv: true,
    targetId: 'profit-and-loss',
    formula: 'Revenue − Expenses = Net Profit (or Loss)',
    description: 'Shows business financial performance and net profitability over a period.',
    usedFor: ['Measure profitability', 'Track sales, COGS, and operating overheads'],
    contains: ['Sales Revenue', 'Cost of Goods Sold', 'Operating Expenses', 'Net Profit'],
  },
  {
    name: 'DayBook Audit Log',
    category: 'advanced',
    hasCsv: true,
    targetId: 'day-book',
    description: 'Chronological list of every accounting transaction posted to the ledger.',
    usedFor: ['Audit and trace daily transactions', 'Inspect sequential journal vouchers'],
    contains: ['Date', 'Voucher Number', 'Account Name', 'Debit', 'Credit'],
  },
  {
    name: 'Cash Flow Statement',
    category: 'advanced',
    badge: 'Core',
    hasCsv: true,
    targetId: 'cash-flow',
    description: 'Tracks cash movement across operating, investing, and financing activities.',
    usedFor: ['Understand cash inflows and outflows', 'Evaluate operational liquidity'],
    contains: ['Operating Activities', 'Investing Activities', 'Financing Activities', 'Net Change in Cash'],
  },
  {
    name: 'All Ledgers Master Report',
    category: 'advanced',
    badge: 'Core',
    hasCsv: true,
    targetId: 'ledger-master',
    description: 'Complete list of ledger accounts in the Chart of Accounts with balances.',
    usedFor: ['Review the entire accounting structure', 'Audit ledger account classifications'],
    contains: ['Ledger Name', 'Primary Group', 'Subgroup', 'Opening Balance', 'Closing Balance'],
  },

  // ── Inventory & Operations Reports ─────────────────────────────────────────
  {
    name: 'Delivery Challans Register',
    category: 'other',
    hasCsv: true,
    targetId: 'sales-register',
    description: 'Shows all delivery challans issued for goods movement without immediate billing.',
    usedFor: ['Track goods delivered before or without invoicing', 'Verify warehouse stock dispatches'],
    contains: ['Challan Number', 'Customer', 'Items', 'Quantity', 'Delivery Status'],
  },
  {
    name: 'Product-wise Profitability',
    category: 'other',
    hasCsv: true,
    targetId: 'profit-and-loss',
    formula: 'Profit = Sales − Cost',
    description: 'Calculates gross profit and margin percentage for each product SKU.',
    usedFor: ['Identify the most and least profitable products', 'Optimize product pricing & discounts'],
    contains: ['Product Name', 'Sales Revenue', 'Cost Price', 'Gross Profit', 'Margin %'],
  },
  {
    name: 'Project-wise Financial P&L',
    category: 'other',
    hasCsv: true,
    targetId: 'profit-and-loss',
    description: 'Shows profit and loss for individual projects or cost centers.',
    usedFor: ['Measure project profitability', 'Track project revenue & direct costs'],
    contains: ['Project Name / Code', 'Project Revenue', 'Project Expenses', 'Net Profit'],
  },
  {
    name: 'Order Management Audit',
    category: 'other',
    hasCsv: true,
    targetId: 'sales-register',
    description: 'Tracks the complete lifecycle of customer orders from creation to delivery.',
    usedFor: ['Monitor order fulfillment and identify delays', 'Audit order processing stages'],
    contains: ['Order Created', 'Confirmed', 'Packed', 'Shipped', 'Delivered', 'Closed'],
  },

  // ── Statutory GST Reports ──────────────────────────────────────────────────
  {
    name: 'GSTR-1 Outward Return',
    category: 'gstr',
    badge: 'GST',
    hasCsv: true,
    targetId: 'gstr-1',
    description: 'Official report of outward taxable supplies submitted to the tax authorities.',
    usedFor: ['Monthly GST return for sales', 'Upload B2B & B2C invoice data to GST portal'],
    contains: ['B2B Sales', 'B2C Sales', 'Exports', 'Credit/Debit Notes', 'HSN Summary'],
  },
  {
    name: 'GSTR-2B Auto ITC Statement',
    category: 'gstr',
    badge: 'GST',
    hasCsv: true,
    targetId: 'gstr-2b',
    description: 'Auto-generated statement of eligible Input Tax Credit based on suppliers GST filings.',
    usedFor: ['Verify ITC before claiming it in returns', 'Reconcile supplier filings'],
    contains: ['Supplier GSTIN', 'Invoice Ref', 'Taxable Value', 'Eligible CGST/SGST/IGST'],
  },
  {
    name: 'GSTR-3B Self Summary',
    category: 'gstr',
    badge: 'GST',
    hasCsv: true,
    targetId: 'gstr-3b',
    description: 'Monthly GST summary return detailing tax liabilities and ITC claimed.',
    usedFor: ['Calculate and pay monthly GST', 'Submit statutory self summary return'],
    contains: ['Outward Supplies', 'Inward Supplies (RCM)', 'ITC Claimed', 'Tax Payable', 'Tax Paid'],
  },
];

export function FinancialReportsDashboard({ onSelectReport }: FinancialReportsDashboardProps) {
  const [dateRange] = useState('2026-07-01 - 2026-07-31');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<'all' | 'ar' | 'ap' | 'advanced' | 'gstr'>('all');
  const [selectedInfoReport, setSelectedInfoReport] = useState<ReportItem | null>(null);

  // Accordion toggle states
  const [isInvoicesOpen, setIsInvoicesOpen] = useState(true);
  const [isPurchasesOpen, setIsPurchasesOpen] = useState(true);
  const [isOrdersOpen, setIsOrdersOpen] = useState(true);

  // Fetch dynamic summary figures from API
  const { data: summaryData } = useQuery({
    queryKey: ['accounts', 'reports-summary-dashboard', dateRange],
    queryFn: async () => {
      try {
        const { data } = await api.get('/api/accounts/reports/summary');
        return data;
      } catch {
        return null;
      }
    }
  });

  const invoicesCount = summaryData?.invoicesCount || 0;
  const invoicesTotal = summaryData?.invoicesTotal || 0;
  const invoicesDue = summaryData?.invoicesDue || 0;
  const invoicesPaid = summaryData?.invoicesPaid || 0;
  const gstTotal = summaryData?.gstTotal || 0;

  const purchasesCount = summaryData?.purchasesCount || 0;
  const purchasesTotal = summaryData?.purchasesTotal || 0;
  const purchasesDue = summaryData?.purchasesDue || 0;
  const purchasesPaid = summaryData?.purchasesPaid || 0;

  const poCount = summaryData?.poCount || 0;
  const poTotal = summaryData?.poTotal || 0;

  const formatMoney = (val: number) =>
    `₹${val.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

  const exportCSV = (title: string) => {
    const csvContent = `Report,GeneratedAt\n${title},${new Date().toISOString()}\n`;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/\s+/g, '_')}_Report.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleOpenReport = (id: string) => {
    if (onSelectReport) {
      onSelectReport(id);
    }
  };

  // Filter reports dynamically based on search query & active category tab
  const filteredReports = useMemo(() => {
    return ALL_REPORTS.filter((rep) => {
      const matchesCategory = activeCategory === 'all' || rep.category === activeCategory;
      const matchesSearch =
        searchQuery.trim() === '' ||
        rep.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (rep.description && rep.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
        rep.targetId.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesCategory && matchesSearch;
    });
  }, [searchQuery, activeCategory]);

  const categories = [
    { id: 'all', label: 'All Reports', count: ALL_REPORTS.length, icon: Layers },
    { id: 'ar', label: 'Receivables (AR)', count: ALL_REPORTS.filter(r => r.category === 'ar').length, icon: Receipt },
    { id: 'ap', label: 'Payables (AP)', count: ALL_REPORTS.filter(r => r.category === 'ap').length, icon: ShoppingBag },
    { id: 'advanced', label: 'Core Statements', count: ALL_REPORTS.filter(r => r.category === 'advanced').length, icon: BarChart3 },
    { id: 'gstr', label: 'GST Returns', count: ALL_REPORTS.filter(r => r.category === 'gstr').length, icon: Sparkles },
  ];

  // Calculate invoice collection percentage
  const collectionPct = invoicesTotal > 0 ? Math.min(100, Math.round((invoicesPaid / invoicesTotal) * 100)) : 0;
  const purchasePaymentPct = purchasesTotal > 0 ? Math.min(100, Math.round((purchasesPaid / purchasesTotal) * 100)) : 0;

  return (
    <div className="space-y-6 pb-8">
      {/* ── Summaries Section (Accordions with Rich KPIs) ───────────────────── */}
      <div className="space-y-4">
        {/* 1. Invoices Summary Accordion */}
        <div className="bg-white dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden transition-all">
          <button
            onClick={() => setIsInvoicesOpen(!isInvoicesOpen)}
            className="w-full px-6 py-4 flex items-center justify-between bg-slate-50 dark:from-slate-900/80 dark:to-indigo-950/20 hover:from-slate-100 dark:hover:from-slate-900 transition-all border-b border-slate-200/60 dark:border-slate-800"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-600/10 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400 flex items-center justify-center font-bold">
                <Receipt className="w-5 h-5" />
              </div>
              <div className="text-left">
                <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <span>Invoices & Receivables Summary</span>
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-medium">
                    {invoicesCount} Invoices
                  </span>
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Total Billed: <span className="font-semibold text-slate-700 dark:text-slate-300">{formatMoney(invoicesTotal)}</span> • Collected: <span className="font-semibold text-emerald-600">{formatMoney(invoicesPaid)}</span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Progress pill */}
              <div className="hidden sm:flex items-center gap-2 bg-slate-100 dark:bg-slate-800 px-3 py-1 rounded-full text-xs font-semibold text-slate-700 dark:text-slate-300">
                <span>Collected:</span>
                <span className="text-emerald-600 dark:text-emerald-400">{collectionPct}%</span>
              </div>
              <div className="p-1.5 rounded-lg bg-slate-200/60 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                {isInvoicesOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </div>
          </button>

          {isInvoicesOpen && (
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                <div className="bg-slate-50 dark:bg-slate-900/60 p-3.5 rounded-xl border border-slate-200/80 dark:border-slate-800 hover:border-blue-300 transition-colors">
                  <div className="flex items-center justify-between text-xs text-slate-500 font-medium">
                    <span>Total Invoices</span>
                    <Info className="w-3.5 h-3.5 text-slate-400" />
                  </div>
                  <p className="text-xl font-black text-slate-900 dark:text-slate-100 mt-1.5">{invoicesCount}</p>
                  <p className="text-[10px] text-blue-600 dark:text-blue-400 font-medium mt-1">Active Billing Log</p>
                </div>

                <div className="bg-slate-50 dark:bg-slate-900/60 p-3.5 rounded-xl border border-slate-200/80 dark:border-slate-800 hover:border-blue-300 transition-colors">
                  <div className="flex items-center justify-between text-xs text-slate-500 font-medium">
                    <span>Total Revenue</span>
                    <TrendingUp className="w-3.5 h-3.5 text-blue-500" />
                  </div>
                  <p className="text-lg font-black text-slate-900 dark:text-slate-100 mt-1.5">{formatMoney(invoicesTotal)}</p>
                  <p className="text-[10px] text-slate-500 font-medium mt-1">Gross Invoice Value</p>
                </div>

                <div className="bg-amber-50/50 dark:bg-amber-950/20 p-3.5 rounded-xl border border-amber-200/80 dark:border-amber-900/40 hover:border-amber-300 transition-colors">
                  <div className="flex items-center justify-between text-xs text-amber-700 dark:text-amber-400 font-medium">
                    <span>Amount Due</span>
                    <Info className="w-3.5 h-3.5 text-amber-500" />
                  </div>
                  <p className="text-lg font-black text-amber-800 dark:text-amber-300 mt-1.5">{formatMoney(invoicesDue)}</p>
                  <p className="text-[10px] text-amber-600 dark:text-amber-400 font-medium mt-1">Outstanding Debtors</p>
                </div>

                <div className="bg-emerald-50/50 dark:bg-emerald-950/20 p-3.5 rounded-xl border border-emerald-200/80 dark:border-emerald-900/40 hover:border-emerald-300 transition-colors">
                  <div className="flex items-center justify-between text-xs text-emerald-700 dark:text-emerald-400 font-medium">
                    <span>Payments Received</span>
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                  </div>
                  <p className="text-lg font-black text-emerald-800 dark:text-emerald-300 mt-1.5">{formatMoney(invoicesPaid)}</p>
                  <p className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium mt-1">{collectionPct}% Realized</p>
                </div>

                <div className="bg-slate-50 dark:bg-slate-900/60 p-3.5 rounded-xl border border-slate-200/80 dark:border-slate-800 hover:border-purple-300 transition-colors">
                  <div className="flex items-center justify-between text-xs text-slate-500 font-medium">
                    <span>GST Output Tax</span>
                    <Sparkles className="w-3.5 h-3.5 text-purple-500" />
                  </div>
                  <p className="text-lg font-black text-purple-700 dark:text-purple-300 mt-1.5">{formatMoney(gstTotal)}</p>
                  <p className="text-[10px] text-purple-600 dark:text-purple-400 font-medium mt-1">Collected Tax</p>
                </div>

                <div className="bg-slate-50 dark:bg-slate-900/60 p-3.5 rounded-xl border border-slate-200/80 dark:border-slate-800">
                  <div className="flex items-center justify-between text-xs text-slate-500 font-medium">
                    <span>Txn Charges</span>
                    <Info className="w-3.5 h-3.5 text-slate-400" />
                  </div>
                  <p className="text-lg font-black text-slate-900 dark:text-slate-100 mt-1.5">₹0</p>
                  <p className="text-[10px] text-slate-400 font-medium mt-1">Payment Gateway</p>
                </div>

                <div className="bg-slate-50 dark:bg-slate-900/60 p-3.5 rounded-xl border border-slate-200/80 dark:border-slate-800">
                  <div className="flex items-center justify-between text-xs text-slate-500 font-medium">
                    <span>TDS Deducted</span>
                    <Info className="w-3.5 h-3.5 text-slate-400" />
                  </div>
                  <p className="text-lg font-black text-slate-900 dark:text-slate-100 mt-1.5">₹0</p>
                  <p className="text-[10px] text-slate-400 font-medium mt-1">Sec 194C / 194J</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 2. Purchases and Expenses Summary Accordion */}
        <div className="bg-white dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden transition-all">
          <button
            onClick={() => setIsPurchasesOpen(!isPurchasesOpen)}
            className="w-full px-6 py-4 flex items-center justify-between bg-slate-50 dark:from-slate-900/80 dark:to-purple-950/20 hover:from-slate-100 dark:hover:from-slate-900 transition-all border-b border-slate-200/60 dark:border-slate-800"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-purple-600/10 text-purple-600 dark:bg-purple-500/20 dark:text-purple-400 flex items-center justify-center font-bold">
                <ShoppingBag className="w-5 h-5" />
              </div>
              <div className="text-left">
                <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <span>Purchases & Operational Expenses</span>
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 font-medium">
                    {purchasesCount} Bills
                  </span>
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Total Purchases: <span className="font-semibold text-slate-700 dark:text-slate-300">{formatMoney(purchasesTotal)}</span> • Paid: <span className="font-semibold text-emerald-600">{formatMoney(purchasesPaid)}</span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 bg-slate-100 dark:bg-slate-800 px-3 py-1 rounded-full text-xs font-semibold text-slate-700 dark:text-slate-300">
                <span>Disbursed:</span>
                <span className="text-purple-600 dark:text-purple-400">{purchasePaymentPct}%</span>
              </div>
              <div className="p-1.5 rounded-lg bg-slate-200/60 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                {isPurchasesOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </div>
          </button>

          {isPurchasesOpen && (
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-slate-50 dark:bg-slate-900/60 p-4 rounded-xl border border-slate-200/80 dark:border-slate-800">
                  <div className="flex items-center justify-between text-xs text-slate-500 font-medium">
                    <span>Total Vendor Bills</span>
                    <Info className="w-3.5 h-3.5 text-slate-400" />
                  </div>
                  <p className="text-xl font-black text-slate-900 dark:text-slate-100 mt-1.5">{purchasesCount}</p>
                  <p className="text-[10px] text-purple-600 font-medium mt-1">Inward Supplies</p>
                </div>

                <div className="bg-slate-50 dark:bg-slate-900/60 p-4 rounded-xl border border-slate-200/80 dark:border-slate-800">
                  <div className="flex items-center justify-between text-xs text-slate-500 font-medium">
                    <span>Total Purchase Value</span>
                    <Info className="w-3.5 h-3.5 text-slate-400" />
                  </div>
                  <p className="text-lg font-black text-slate-900 dark:text-slate-100 mt-1.5">{formatMoney(purchasesTotal)}</p>
                  <p className="text-[10px] text-slate-500 font-medium mt-1">Gross Cost</p>
                </div>

                <div className="bg-rose-50/50 dark:bg-rose-950/20 p-4 rounded-xl border border-rose-200/80 dark:border-rose-900/40">
                  <div className="flex items-center justify-between text-xs text-rose-700 dark:text-rose-400 font-medium">
                    <span>Vendor Payables Due</span>
                    <Info className="w-3.5 h-3.5 text-rose-500" />
                  </div>
                  <p className="text-lg font-black text-rose-800 dark:text-rose-300 mt-1.5">{formatMoney(purchasesDue)}</p>
                  <p className="text-[10px] text-rose-600 dark:text-rose-400 font-medium mt-1">Outstanding Payables</p>
                </div>

                <div className="bg-emerald-50/50 dark:bg-emerald-950/20 p-4 rounded-xl border border-emerald-200/80 dark:border-emerald-900/40">
                  <div className="flex items-center justify-between text-xs text-emerald-700 dark:text-emerald-400 font-medium">
                    <span>Payments Made</span>
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                  </div>
                  <p className="text-lg font-black text-emerald-800 dark:text-emerald-300 mt-1.5">{formatMoney(purchasesPaid)}</p>
                  <p className="text-[10px] text-emerald-600 font-medium mt-1">Settled Vendor Bills</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 3. Purchase Order Summary Accordion */}
        <div className="bg-white dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden transition-all">
          <button
            onClick={() => setIsOrdersOpen(!isOrdersOpen)}
            className="w-full px-6 py-4 flex items-center justify-between bg-slate-50 dark:from-slate-900/80 dark:to-emerald-950/20 hover:from-slate-100 dark:hover:from-slate-900 transition-all border-b border-slate-200/60 dark:border-slate-800"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-emerald-600/10 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400 flex items-center justify-center font-bold">
                <ShoppingCart className="w-5 h-5" />
              </div>
              <div className="text-left">
                <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <span>Purchase Orders Commitments</span>
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 font-medium">
                    {poCount} Orders
                  </span>
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Total Committed Value: <span className="font-semibold text-slate-700 dark:text-slate-300">{formatMoney(poTotal)}</span>
                </p>
              </div>
            </div>
            <div className="p-1.5 rounded-lg bg-slate-200/60 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
              {isOrdersOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </div>
          </button>

          {isOrdersOpen && (
            <div className="p-5">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-slate-50 dark:bg-slate-900/60 p-4 rounded-xl border border-slate-200/80 dark:border-slate-800">
                  <div className="flex items-center justify-between text-xs text-slate-500 font-medium">
                    <span>Open PO Count</span>
                    <Info className="w-3.5 h-3.5 text-slate-400" />
                  </div>
                  <p className="text-xl font-black text-slate-900 dark:text-slate-100 mt-1.5">{poCount}</p>
                  <p className="text-[10px] text-emerald-600 font-medium mt-1">Pending Fulfillment</p>
                </div>

                <div className="bg-slate-50 dark:bg-slate-900/60 p-4 rounded-xl border border-slate-200/80 dark:border-slate-800">
                  <div className="flex items-center justify-between text-xs text-slate-500 font-medium">
                    <span>Committed Amount</span>
                    <Info className="w-3.5 h-3.5 text-slate-400" />
                  </div>
                  <p className="text-lg font-black text-slate-900 dark:text-slate-100 mt-1.5">{formatMoney(poTotal)}</p>
                  <p className="text-[10px] text-slate-500 font-medium mt-1">Contractual PO Total</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Categorized Reports Directory Section ───────────────────────────── */}
      <div className="space-y-6 pt-4">
        {/* Directory Controls Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-white dark:bg-slate-950 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
          {/* Category Tabs */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 lg:pb-0 scrollbar-none">
            {categories.map((cat) => {
              const Icon = cat.icon;
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id as any)}
                  className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all shrink-0 ${
                    isActive
                      ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/20'
                      : 'bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{cat.label}</span>
                  <span
                    className={`px-1.5 py-0.5 rounded-full text-[10px] font-mono ${
                      isActive ? 'bg-white/20 text-white' : 'bg-slate-200 dark:bg-slate-800 text-slate-500'
                    }`}
                  >
                    {cat.count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Search Box */}
          <div className="relative w-full lg:w-72">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search reports by name or keyword..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 hover:text-slate-600"
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/* Reports Grid */}
        {filteredReports.length === 0 ? (
          <div className="bg-white dark:bg-slate-950 p-12 text-center rounded-2xl border border-slate-200 dark:border-slate-800">
            <Filter className="w-8 h-8 text-slate-300 mx-auto mb-3" />
            <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200">No reports found matching your criteria</h4>
            <p className="text-xs text-slate-500 mt-1">Try adjusting your search terms or select a different category filter.</p>
            <button
              onClick={() => { setSearchQuery(''); setActiveCategory('all'); }}
              className="mt-4 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl text-xs font-semibold hover:bg-indigo-100 transition-colors"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredReports.map((rep) => (
              <div
                key={rep.name}
                className="group relative bg-white dark:bg-slate-950 hover:bg-slate-50/80 dark:hover:bg-slate-900/80 p-4 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-2xs hover:shadow-md transition-all flex flex-col justify-between gap-3 hover:-translate-y-0.5"
              >
                <div>
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <h5 className="text-sm font-bold text-slate-900 dark:text-slate-100 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors leading-tight">
                      {rep.name}
                    </h5>
                    <div className="flex items-center gap-1 shrink-0">
                      {rep.badge && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800">
                          {rep.badge}
                        </span>
                      )}
                      <button
                        onClick={() => setSelectedInfoReport(rep)}
                        className="p-1 text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/60 rounded-lg transition-colors"
                        title="View Report Details & Purpose"
                      >
                        <Info className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {rep.description && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed">
                      {rep.description}
                    </p>
                  )}
                  {rep.formula && (
                    <div className="mt-2 text-[11px] font-mono bg-indigo-50/70 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 px-2.5 py-1 rounded-lg border border-indigo-200/50 dark:border-indigo-800/50 inline-block font-semibold">
                      {rep.formula}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-slate-900 text-xs">
                  <div className="flex items-center gap-1.5">
                    {(rep.hasCsv || rep.csvOnly) && (
                      <button
                        onClick={() => exportCSV(rep.name)}
                        className="px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center gap-1 text-[11px] font-medium"
                        title="Download CSV"
                      >
                        <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />
                        <span>CSV</span>
                      </button>
                    )}
                    <button
                      onClick={() => setSelectedInfoReport(rep)}
                      className="text-[11px] font-medium text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 underline transition-colors"
                    >
                      Guide
                    </button>
                  </div>

                  {!rep.csvOnly && (
                    <button
                      onClick={() => handleOpenReport(rep.targetId)}
                      className="px-3.5 py-1.5 rounded-xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-600 hover:text-white dark:hover:bg-indigo-600 dark:hover:text-white font-semibold transition-all flex items-center gap-1 text-xs group/btn shadow-2xs"
                    >
                      <span>View Report</span>
                      <ArrowUpRight className="w-3.5 h-3.5 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Report Explanation Guide Modal ────────────────────────────────────── */}
      {selectedInfoReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div
            className="bg-white dark:bg-slate-950 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 w-full max-w-lg overflow-hidden space-y-0"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-slate-900 p-5 text-white flex items-start justify-between gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="p-1.5 rounded-lg bg-indigo-500/20 text-indigo-300 border border-indigo-400/30">
                    <BookOpen className="w-4 h-4" />
                  </span>
                  <span className="text-xs font-semibold text-indigo-300 uppercase tracking-wide">
                    ERP Report Documentation
                  </span>
                </div>
                <h3 className="text-lg font-extrabold text-white flex items-center gap-2">
                  <span>{selectedInfoReport.name}</span>
                  {selectedInfoReport.badge && (
                    <span className="text-[10px] bg-indigo-500/30 text-indigo-200 border border-indigo-400/40 px-2 py-0.5 rounded-full font-bold">
                      {selectedInfoReport.badge}
                    </span>
                  )}
                </h3>
              </div>
              <button
                onClick={() => setSelectedInfoReport(null)}
                className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content Body */}
            <div className="p-6 space-y-5 max-h-[75vh] overflow-y-auto">
              {/* Description */}
              <div className="space-y-1.5">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Overview</h4>
                <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed bg-slate-50 dark:bg-slate-900/60 p-3.5 rounded-xl border border-slate-100 dark:border-slate-800/80">
                  {selectedInfoReport.description}
                </p>
              </div>

              {/* Formula if applicable */}
              {selectedInfoReport.formula && (
                <div className="space-y-1.5">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Accounting Formula</h4>
                  <div className="p-3 bg-indigo-50/80 dark:bg-indigo-950/60 border border-indigo-200 dark:border-indigo-800 rounded-xl text-xs font-mono font-bold text-indigo-800 dark:text-indigo-300">
                    {selectedInfoReport.formula}
                  </div>
                </div>
              )}

              {/* Used For Checklist */}
              {selectedInfoReport.usedFor && selectedInfoReport.usedFor.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Used For</h4>
                  <ul className="space-y-2">
                    {selectedInfoReport.usedFor.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-xs text-slate-700 dark:text-slate-300">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Contains / Key Fields Tags */}
              {selectedInfoReport.contains && selectedInfoReport.contains.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Report Contains / Key Fields</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedInfoReport.contains.map((field, idx) => (
                      <span
                        key={idx}
                        className="text-xs bg-slate-100 dark:bg-slate-900 text-slate-700 dark:text-slate-300 px-2.5 py-1 rounded-lg border border-slate-200/80 dark:border-slate-800 font-medium"
                      >
                        {field}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer Actions */}
            <div className="p-4 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between gap-3">
              <div>
                {(selectedInfoReport.hasCsv || selectedInfoReport.csvOnly) && (
                  <button
                    onClick={() => exportCSV(selectedInfoReport.name)}
                    className="px-3 py-2 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-colors shadow-2xs"
                  >
                    <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                    <span>Export CSV</span>
                  </button>
                )}
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSelectedInfoReport(null)}
                  className="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-xl transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    const id = selectedInfoReport.targetId;
                    setSelectedInfoReport(null);
                    handleOpenReport(id);
                  }}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl text-xs flex items-center gap-1.5 transition-colors shadow-md shadow-indigo-500/20"
                >
                  <span>Open Report</span>
                  <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
